/**
 * Hook for managing inventory valuation methods
 * 
 * Provides access to the current valuation method and related calculations.
 * Reads from Stock Options settings.
 */

import { useState, useEffect } from 'react';
import * as CostingService from '@/services/costing-service';

export type ValuationMethod = 'average' | 'fifo' | 'standard';

interface StockOptions {
  valuationMethod: ValuationMethod;
  allowPerItemOverride: boolean;
}

/**
 * Hook to get and use the current valuation method
 */
export const useValuationMethod = () => {
  const [valuationMethod, setValuationMethod] = useState<ValuationMethod>('average');
  const [allowPerItemOverride, setAllowPerItemOverride] = useState(false);

  // Load valuation method from stock options
  useEffect(() => {
    const loadOptions = () => {
      const saved = localStorage.getItem('stockOptions');
      if (saved) {
        try {
          const options = JSON.parse(saved);
          setValuationMethod(options.valuationMethod || 'average');
          setAllowPerItemOverride(options.allowPerItemOverride || false);
        } catch (error) {
          console.error('Failed to load stock options:', error);
        }
      }
    };

    loadOptions();

    // Listen for changes to stock options
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'stockOptions') {
        loadOptions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Calculate COGS based on the current valuation method
   */
  const calculateCOGS = (
    stockCode: string,
    warehouseCode: string,
    quantity: number,
    averageCost: number
  ): { totalCost: number; averageCost: number; method: ValuationMethod } => {
    if (valuationMethod === 'fifo') {
      const result = CostingService.calculateFIFOCOGS(stockCode, warehouseCode, quantity);
      return {
        totalCost: result.totalCost,
        averageCost: result.averageCost,
        method: 'fifo',
      };
    } else if (valuationMethod === 'average') {
      const totalCost = CostingService.calculateCOGS(quantity, averageCost);
      return {
        totalCost,
        averageCost,
        method: 'average',
      };
    } else {
      // Standard cost - not yet implemented
      const totalCost = CostingService.calculateCOGS(quantity, averageCost);
      return {
        totalCost,
        averageCost,
        method: 'standard',
      };
    }
  };

  /**
   * Add a purchase/receipt based on the current valuation method
   */
  const addPurchase = (
    stockCode: string,
    warehouseCode: string,
    locationCode: string | undefined,
    quantity: number,
    unitCost: number,
    currentQty: number,
    currentAvgCost: number,
    reference?: string
  ): { newAverageCost: number; method: ValuationMethod } => {
    if (valuationMethod === 'fifo') {
      // Add a new cost layer
      CostingService.addFIFOCostLayer(
        stockCode,
        warehouseCode,
        locationCode,
        quantity,
        unitCost,
        reference
      );
      
      // For FIFO, we still track weighted average for reporting
      const newAvgCost = CostingService.calculateAverageCost(
        currentQty,
        currentAvgCost,
        quantity,
        unitCost
      );
      
      return {
        newAverageCost: newAvgCost,
        method: 'fifo',
      };
    } else if (valuationMethod === 'average') {
      // Calculate new weighted average
      const newAvgCost = CostingService.calculateAverageCost(
        currentQty,
        currentAvgCost,
        quantity,
        unitCost
      );
      
      return {
        newAverageCost: newAvgCost,
        method: 'average',
      };
    } else {
      // Standard cost - cost doesn't change on purchase
      return {
        newAverageCost: currentAvgCost,
        method: 'standard',
      };
    }
  };

  /**
   * Get the current cost for a stock item based on valuation method
   */
  const getCurrentCost = (
    stockCode: string,
    warehouseCode: string,
    averageCost: number
  ): number => {
    if (valuationMethod === 'fifo') {
      return CostingService.getCurrentFIFOCost(stockCode, warehouseCode) || averageCost;
    } else {
      return averageCost;
    }
  };

  /**
   * Get valuation method display name
   */
  const getMethodDisplayName = (): string => {
    switch (valuationMethod) {
      case 'average':
        return 'Average Cost (Weighted Average)';
      case 'fifo':
        return 'FIFO (First In, First Out)';
      case 'standard':
        return 'Standard Cost';
      default:
        return 'Unknown';
    }
  };

  return {
    valuationMethod,
    allowPerItemOverride,
    calculateCOGS,
    addPurchase,
    getCurrentCost,
    getMethodDisplayName,
  };
};
